import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import {
  FileText, TrendingUp, Settings, LogOut, Plug, Menu, X, PanelLeftClose, PanelLeftOpen
} from "lucide-react";

const Sidebar = ({ user, logout }) => {
  const location = useLocation();
  const { mobileMenuOpen, setMobileMenuOpen } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [setMobileMenuOpen]);

  const navItems = [
    { path: '/dashboard', icon: TrendingUp, label: 'Dashboard' },
    { path: '/proposals', icon: FileText, label: 'Proposals' },
    { path: '/integrations', icon: Plug, label: 'Integrations' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (path) => location.pathname === path;

  const NavContent = ({ mobile = false }) => (
    <>
      {/* Logo */}
      <Link to="/" className="flex items-center gap-2 mb-8 lg:mb-10">
        <div className={`${isCollapsed && !mobile ? 'w-8 h-8' : 'w-8 h-8 lg:w-10 lg:h-10'} bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0`}>
          <FileText className={`${isCollapsed && !mobile ? 'w-4 h-4' : 'w-4 h-4 lg:w-5 lg:h-5'} text-white`} />
        </div>
        {(!isCollapsed || mobile) && (
          <span className="font-display text-base lg:text-lg font-semibold whitespace-nowrap">ProspectConnect</span>
        )}
      </Link>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 lg:space-y-2">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 lg:py-3 rounded-lg transition-colors text-sm lg:text-base ${
              isActive(item.path)
                ? 'bg-white/10 text-white'
                : 'hover:bg-white/5 text-white/70 hover:text-white'
            } ${isCollapsed && !mobile ? 'justify-center px-2' : ''}`}
            title={isCollapsed && !mobile ? item.label : undefined}
          >
            <item.icon className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0" />
            {(!isCollapsed || mobile) && <span>{item.label}</span>}
          </Link>
        ))}
      </nav>

      {/* User section */}
      <div className="border-t border-white/10 pt-4">
        <div className={`flex items-center gap-2 lg:gap-3 mb-3 lg:mb-4 ${isCollapsed && !mobile ? 'justify-center' : ''}`}>
          <div className={`${isCollapsed && !mobile ? 'w-8 h-8' : 'w-8 h-8 lg:w-10 lg:h-10'} bg-white/10 rounded-full flex items-center justify-center flex-shrink-0`}>
            <span className="text-sm lg:text-lg font-semibold">{user?.name?.charAt(0)}</span>
          </div>
          {(!isCollapsed || mobile) && (
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate text-sm lg:text-base">{user?.name}</p>
              <p className="text-xs text-white/50 truncate">{user?.email}</p>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          className={`w-full text-white/70 hover:text-white hover:bg-white/5 text-sm lg:text-base ${
            isCollapsed && !mobile ? 'justify-center px-2' : 'justify-start'
          }`}
          onClick={logout}
          data-testid="logout-btn"
          title={isCollapsed && !mobile ? 'Sign Out' : undefined}
        >
          <LogOut className="w-4 h-4 flex-shrink-0" />
          {(!isCollapsed || mobile) && <span className="ml-2">Sign Out</span>}
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger button - hide when sidebar is open */}
      {!mobileMenuOpen && (
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="md:hidden fixed top-4 left-4 z-40 p-2 bg-forest text-white rounded-lg shadow-lg"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`md:hidden fixed left-0 top-0 h-screen w-64 bg-forest text-white flex flex-col z-50 transform transition-transform duration-300 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Mobile header with close button */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <span className="font-display text-base font-semibold">ProspectConnect</span>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            data-testid="mobile-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Mobile nav content */}
        <div className="flex-1 p-4 flex flex-col">
          <nav className="flex-1 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-base ${
                  isActive(item.path)
                    ? 'bg-white/10 text-white'
                    : 'hover:bg-white/5 text-white/70 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* User section */}
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                <span className="text-lg font-semibold">{user?.name?.charAt(0)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-xs text-white/50 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={logout}
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex fixed left-0 top-0 h-screen bg-forest text-white p-4 lg:p-6 flex-col transition-all duration-300 ${
          isCollapsed ? 'w-16' : 'w-52 lg:w-64'
        }`}
      >
        {/* Logo + Collapse toggle row */}
        <div className={`flex items-center mb-6 ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
          {!isCollapsed && (
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
              </div>
              <span className="font-display text-base lg:text-lg font-semibold whitespace-nowrap">ProspectConnect</span>
            </Link>
          )}
          
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="collapse-sidebar-btn"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <PanelLeftOpen className="w-5 h-5" />
            ) : (
              <PanelLeftClose className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 lg:space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 lg:py-3 rounded-lg transition-colors text-sm lg:text-base ${
                isActive(item.path)
                  ? 'bg-white/10 text-white'
                  : 'hover:bg-white/5 text-white/70 hover:text-white'
              } ${isCollapsed ? 'justify-center px-2' : ''}`}
              title={isCollapsed ? item.label : undefined}
            >
              <item.icon className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0" />
              {!isCollapsed && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* User section */}
        <div className="border-t border-white/10 pt-4">
          <div className={`flex items-center gap-2 lg:gap-3 mb-3 lg:mb-4 ${isCollapsed ? 'justify-center' : ''}`}>
            <div className={`${isCollapsed ? 'w-8 h-8' : 'w-8 h-8 lg:w-10 lg:h-10'} bg-white/10 rounded-full flex items-center justify-center flex-shrink-0`} title={isCollapsed ? user?.name : undefined}>
              <span className="text-sm lg:text-lg font-semibold">{user?.name?.charAt(0)}</span>
            </div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate text-sm lg:text-base">{user?.name}</p>
                <p className="text-xs text-white/50 truncate">{user?.email}</p>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            className={`w-full text-white/70 hover:text-white hover:bg-white/5 text-sm lg:text-base ${
              isCollapsed ? 'justify-center px-2' : 'justify-start'
            }`}
            onClick={logout}
            data-testid="logout-btn"
            title={isCollapsed ? 'Sign Out' : undefined}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {!isCollapsed && <span className="ml-2">Sign Out</span>}
          </Button>
        </div>
      </aside>

      {/* Spacer for main content */}
      <div className={`hidden md:block flex-shrink-0 transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-52 lg:w-64'}`} />
    </>
  );
};

export default Sidebar;
